/**
 * AnnotationChip (#546, epic #543) — the non-editable summary of a captured brightmark annotation,
 * shown at the top of the FeedbackModal when annotate mode produced a target.
 *
 * Summarises the target: a "Text" / "Element" badge, the source hint (e.g. `data-testid=deal-row`)
 * or the element tag, and a truncated text snippet. A thumbnail of the element screenshot appears once
 * the (async, best-effort) snapdom raster resolves. A ✕ discards the annotation — the modal falls back
 * to a classic submission (no annotation, no auto-attached screenshot).
 */
import { MousePointerClick, TextCursorInput, X } from "lucide-react";
import type { BrightmarkAnnotation } from "../../../lib/brightmark/types";

interface AnnotationChipProps {
  annotation: BrightmarkAnnotation;
  /** Object URL for the element-screenshot thumbnail, or null while it resolves / on failure. */
  thumbnailUrl: string | null;
  onDiscard: () => void;
}

/** A short, human label for what the annotation points at. */
function targetLabel(a: BrightmarkAnnotation): string {
  if (a.source_hint_attr) return a.source_hint_attr;
  // Fall back to the leading tag of the captured HTML, e.g. "<button …>" → "button".
  const tagMatch = a.element_html?.match(/^<([a-zA-Z][\w-]*)/);
  if (tagMatch?.[1]) return tagMatch[1];
  return a.anchor_kind === "text" ? "selected text" : "element";
}

/** A truncated text snippet for the chip body (quote for text anchors, snippet otherwise). */
function snippetText(a: BrightmarkAnnotation): string | null {
  const raw = a.anchor_kind === "text" ? a.quote_exact : a.text_snippet;
  if (!raw) return null;
  const flat = raw.replace(/\s+/g, " ").trim();
  return flat.length > 80 ? `${flat.slice(0, 80)}…` : flat;
}

export function AnnotationChip({ annotation, thumbnailUrl, onDiscard }: AnnotationChipProps) {
  const isText = annotation.anchor_kind === "text";
  const Icon = isText ? TextCursorInput : MousePointerClick;
  const snippet = snippetText(annotation);

  return (
    <div className="flex items-start gap-3 rounded-lg border-l-[3px] border-l-[var(--color-accent)] border border-[var(--color-border)] bg-[var(--color-bg-page)] p-3">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt="Captured element"
          className="h-12 w-12 shrink-0 rounded object-cover border border-[var(--color-border)]"
        />
      ) : (
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-[var(--color-bg-card)] border border-[var(--color-border)]">
          <Icon className="h-5 w-5 text-[var(--color-accent)]" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-sm font-medium text-[var(--color-text-primary)]">
          <Icon className="h-3.5 w-3.5 text-[var(--color-accent)]" />
          {isText ? "Text" : "Element"} · {targetLabel(annotation)}
        </p>
        {snippet ? (
          <p className="mt-0.5 truncate text-xs text-[var(--color-text-muted)]">{snippet}</p>
        ) : (
          <p className="mt-0.5 truncate text-xs text-[var(--color-text-muted)]">
            {annotation.route}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onDiscard}
        aria-label="Discard annotation"
        title="Remove this annotation"
        className="p-2 -m-1 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
